import { OrderCancelReason, TableVoidReason } from '@pedix-workspace/utils';

export type ProductStockEntityType = 'product' | 'presentation' | 'option-item';

export const productStockSources = [
  'sale',
  'sale-cancel',
  'sale-edit',
  'manual-adjust',
  'import',
  'backfill',
  'count',
  'control-disabled',
  'table-session',
  'table-session-cancel',
  'entry',
] as const;

export type ProductStockSource = (typeof productStockSources)[number];

/**
 * Stock de una entidad stockeable en su colección propia `productStock`.
 * Invariante: **el doc puede ser finito (`stockQty` número, incluido 0) o infinito/sin control
 * (`stockQty: null`)**. La ausencia del doc TAMBIÉN significa infinito (entidad nunca controlada) —
 * ausencia y `null` son equivalentes para todo lector. Persistir el doc con `null` (en vez de
 * borrarlo) al desactivar el control conserva el historial de `productStockMovements` (baja de
 * control ≠ pérdida de historial). doc id = id de la entidad (producto/option-item = doc id de
 * Firestore; presentación = id normalizado). `source`/`uid`/`reason`/`systemReason` describen el
 * último write y los consume el trigger de kardex (no es estado del stock).
 */
export interface ProductStock {
  id: string;
  establishmentId: string;
  productId: string;
  entityType: ProductStockEntityType;
  /** Stock finito (número ≥ 0) o `null` = infinito/sin control (doc conservado por su historial). */
  stockQty: number | null;
  lowStockThreshold?: number | null;
  updated: Date;
  source: ProductStockSource;
  uid?: string | null;
  /** Texto ingresado por el cliente (motivo del ajuste). */
  reason?: string;
  /** Contexto crudo del sistema para debug/kardex (ej. el `_audit.source` de origen en sync con trigger) */
  systemReason?: string;
  /** Última orden que tocó este stock; marcador best-effort que consume el trigger de kardex. */
  lastOrderId?: string;
  /** Número (`sequenceId`) de la última orden que tocó este stock; para mostrar en el kardex sin resolver el pedido. */
  lastOrderSequenceId?: number;
  lastOrderCancelReason?: OrderCancelReason;
  /** Último conteo que tocó este stock; marcador best-effort que consume el trigger de kardex. */
  lastInventoryCountId?: string;
  /** Número (`sequenceId`) del último conteo que tocó este stock; para mostrar en el kardex sin resolver el conteo. */
  lastInventoryCountSequenceId?: number;
  lastTableSessionId?: string;
  lastTableSessionTableName?: string;
  lastTableSessionReason?: TableVoidReason;
  lastEntryId?: string;
  lastEntryName?: string;
}

/**
 * Record de stock que consumen los lectores in-memory (`StockInfo`/`StockMap`, DTO de la API,
 * transacción de venta). `stockQty` puede ser `null` (= sin control = infinito): aunque las fronteras
 * (API front + transacción de venta) descartan los docs con `stockQty: null`, hay caminos que leen
 * `productStock` DIRECTO de Firestore y alimentan `StockService` (p.ej. el order-editor del admin), así
 * que `StockInfo` trata el `null` como infinito de forma **defensiva** (no depende solo de las fronteras).
 */
export type ProductStockRecord = Required<
  Pick<ProductStock, 'id' | 'productId' | 'entityType' | 'stockQty'>
>;

/**
 * Fila del libro de movimientos (`productStockMovements`), que el trigger de kardex deriva de cada
 * write a `productStock`. Inmutable. Al root van solo los campos indexados/consultados; el resto en
 * `details`. El "tipo" para la UI se traduce con la key i18n `stockMovementType.<source>` (sin mapper).
 */
export interface ProductStockMovement {
  id: string;
  establishmentId: string;
  stockItemId: string;
  /** = `productStock.updated` del write que originó el movimiento; ordena el Kardex (tiebreaker `id`). */
  created: Date;
  /** Clasificación del movimiento (discriminador). Al root: se consulta (`where source in [...]`) y es el eje del filtro por tipo. La UI lo traduce con `stockMovementType.<source>`. */
  source: ProductStockSource;
  details: {
    productId: string;
    entityType: ProductStockEntityType;
    delta: number;
    /** Saldo tras el movimiento. `null` = movimiento de baja de control (`control-disabled`): sin saldo. */
    balanceAfter: number | null;
    /** uid responsable; `null` = cliente/sistema. */
    createdBy: string | null;
    reason?: string;
    systemReason?: string;
    orderId?: string;
    /** Número (`sequenceId`) del pedido, para mostrar en el kardex; la navegación sigue usando `orderId`. */
    orderSequenceId?: number;
    orderCancelReason?: OrderCancelReason;
    inventoryCountId?: string;
    /** Número (`sequenceId`) del conteo, para mostrar en el kardex; la navegación sigue usando `inventoryCountId`. */
    inventoryCountSequenceId?: number;
    tableSessionId?: string;
    tableSessionTableName?: string;
    tableSessionReason?: TableVoidReason;
    entryId?: string;
    entryName?: string;
  };
}

export type InventoryEntryLine = {
  stockItemId: string;
  productId: string;
  entityType: ProductStockEntityType;
  productName: string;
  presentationName?: string;
  categoryId?: string;
  categoryName?: string;
  quantity: number;
  stockBefore: number | null;
  unitPrice?: number;
  costBefore?: number | null;
  costAfter?: number;
};

export interface InventoryEntry {
  id: string;
  establishmentId: string;
  created: Date;
  details: {
    name: string;
    note?: string;
    lines: InventoryEntryLine[];
    total?: number;
    createdBy: string;
  };
}

/**
 * Estado del conteo. Solo dos: `in-progress` (contando) y `completed` (terminado). Si el conteo además
 * **ajustó el stock** se distingue por `details.appliedAt` (seteado), no por un estado aparte: un
 * `in-progress` = cargando cantidades (grilla UI o planilla Excel sin subir); `in-review` = cantidades
 * cargadas, viendo la variación (se puede aplicar/cerrar o volver a `in-progress`); `completed` sin
 * `appliedAt` = reportado sin tocar stock (todavía se puede aplicar si es el más reciente); con `appliedAt` =
 * ajuste aplicado (terminal, ya no se descarta ni re-aplica). `in-progress` e `in-review` son ambos activos
 * (bloquean iniciar otro conteo); solo hay 0 o 1 conteo activo por sucursal.
 */
export type InventoryCountStatus = 'in-progress' | 'in-review' | 'completed';
export type InventoryCountScope = 'full' | 'partial';
export type InventoryCountCaptureMethod = 'ui' | 'excel';

export interface InventoryCountSummary {
  totalItems: number;
  countedItems: number;
  matchItems: number;
  shortageItems: number;
  overageItems: number;
}

/**
 * Resumen de la **variación** de un conteo cerrado — los mismos 5 números que el reporte de resultado
 * (Nuevos/Coinciden/Sobrantes/Faltantes/Sin control). Se computa contra el snapshot (`stockAtStart`) al
 * finalizar/aplicar y se persiste para que la lista de conteos muestre el resultado sin cargar el snapshot
 * por cada card (mismo criterio que `hasChanges`). Distinto de `InventoryCountSummary`, que compara contra
 * el `e` mutable y no separa altas ni bajas de control.
 */
export interface VarianceSummary {
  new: number;
  match: number;
  overage: number;
  shortage: number;
  noControl: number;
}

/**
 * Un ítem del conteo: esperado congelado al iniciar (`e`) + contado (`c`).
 * - `e`: stock esperado. `null` = el ítem **no tiene control de stock** (sin `productStock`, infinito) →
 *   el checkbox "Control" arranca destildado. Un número = controlado (checkbox tildado).
 * - `c`: contado. `null` = NO contado (distinto de `0` = contado sin stock).
 * - `a`: **aplicado** — `true` si el write de stock de este ítem ya commiteó (se setea en el MISMO batch que
 *   el write, en `applyInventoryCount`). Es la marca de idempotencia por-chunk: un retry relee el conteo y
 *   saltea los `a === true`, sin depender del signal (posiblemente stale) de `productStock`.
 * Valor mínimo a propósito (protege el presupuesto del doc, ver `InventoryCount`).
 */
export interface InventoryCountItem {
  e: number | null;
  c: number | null;
  a?: boolean;
  /** Motivo de ajuste (nota libre por ítem). Se propaga al `reason` del movimiento al aplicar. */
  n?: string;
}

/**
 * Sesión de toma de inventario — **UN solo documento**. Todo lo no consultado vive en `details`, incluido
 * el mapa `stockItems` (key = id del nodo `productStock`, value = `{ e, c }`), que queda exento de
 * auto-indexado por el `fieldOverride` sobre `details`. Sin colección de líneas (DR-11): los catálogos de
 * Pedix (cientos, a lo sumo pocos miles de ítems) entran holgados en 1 doc (~10k ítems); arriba de un
 * umbral se restringe a conteo por categoría. `completed` es terminal (con o sin `appliedAt`). El nombre a
 * mostrar se deriva de `created` ("Inventario {fecha}"), no se persiste. La estructura/nombres del catálogo
 * y el baseline `hadControl`/`stockAtStart` viven congelados en `InventoryCountCatalogSnapshot` (write-once
 * al iniciar), NO acá.
 */
export interface InventoryCount {
  id: string;
  establishmentId: string;
  status: InventoryCountStatus;
  created: Date;
  updated: Date;
  details: {
    scope: InventoryCountScope;
    scopeCategoryIds?: string[];
    /** Número secuencial posicional del conteo (por establishment), asignado al crear. Se reusa tras descartar. */
    sequenceId: number;
    /** Cómo se cargan las cantidades: `'ui'` (contar en pantalla) o `'excel'` (planilla). Ausente = `'ui'` (legacy). */
    captureMethod?: InventoryCountCaptureMethod;
    includeOptionItems: boolean;
    blindCount: boolean;
    blockOrders: boolean;
    createdBy: string;
    /** Cuándo pasó a `completed` (se finalizó el conteo). */
    closedAt?: Date;
    /** Cuándo se aplicó el ajuste de stock. `null`/ausente = completado sin aplicar (aún aplicable). */
    appliedAt?: Date;
    /**
     * `true` si aplicar el conteo cambiaría el stock (altas, bajas de control o variaciones ≠ 0). Se computa
     * contra el snapshot al **finalizar** (`completeInventoryCount`), para que la lista distinga "sin
     * diferencias" sin cargar el snapshot por cada conteo. Ausente en conteos previos a esta versión.
     */
    hasChanges?: boolean;
    summary: InventoryCountSummary;
    /**
     * Variación del conteo (Nuevos/Coinciden/Sobrantes/Faltantes/Sin control) computada contra el snapshot al
     * finalizar/aplicar. La lista de conteos la usa para mostrar el resultado sin cargar el snapshot. Ausente
     * mientras el conteo está `in-progress` y en conteos previos a esta versión.
     */
    varianceSummary?: VarianceSummary;
    stockItems: Record<string, InventoryCountItem>;
  };
}

/** Una presentación congelada en el snapshot del conteo. `stock = null` ⇒ sin control (infinito) al iniciar. */
export interface InventoryCountSnapshotPresentation {
  id: string;
  name: string;
  /** Denormalizado al iniciar el conteo (solo display). Ausente en snapshots viejos ⇒ tratar como ''. */
  sku?: string;
  /** Stock al iniciar. `null` = sin control (infinito) ⇒ `hadControl=false` para esta presentación. */
  stock: number | null;
}

/** Un producto/ítem congelado en el snapshot del conteo (fila de la grilla de contar y de la variación). */
export interface InventoryCountSnapshotProduct {
  id: string;
  name: string;
  /** Distingue adicional (option-item) de producto/presentación → `entityType` en el apply. */
  isOptionItem: boolean;
  /** Visible en el catálogo del comprador. `false` = inactivo (se cuenta igual, badge eye-slash). */
  active: boolean;
  /** Miniatura para la grilla de contar. Vacío si el producto/ítem no tiene imagen. */
  thumbnail: string;
  sku: string;
  /** Agrupador (`Product.groupName`): la grilla de Contar en desktop agrupa por `product-group`. */
  groupName: string;
  /**
   * Stock al iniciar del NODO producto (solo productos SIN presentaciones). `null` = sin control.
   * En productos CON presentaciones el stock vive en cada presentación → acá va `null`.
   */
  stock: number | null;
  presentations?: InventoryCountSnapshotPresentation[];
}

/**
 * Snapshot inmutable del catálogo al iniciar un conteo — **UN doc por categoría en scope** (incluida la
 * categoría ficticia de adicionales). Write-once en `startInventoryCount` y cascade-delete al descartar; es
 * la **fuente ÚNICA** para renderizar TODA la vista de conteo (Contar + Variación + apply), así el flujo no
 * vuelve a tocar `catalog`/`productStock` y una vista vieja queda fiel al momento del conteo aunque el
 * catálogo cambie después. El array `products` (dentro de `details`) queda exento de auto-indexado por el
 * `fieldOverride` sobre `details`. Doc id determinístico `{inventoryCountId}_{categoryId}` (idempotencia al
 * iniciar + cascade sin query).
 */
export interface InventoryCountCatalogSnapshot {
  id: string;
  establishmentId: string;
  inventoryCountId: string;
  created: Date;
  createdBy: string;
  details: {
    /** Categoría real, o la ficticia de adicionales (`${establishmentId}-items`). */
    categoryId: string;
    categoryName: string;
    /** `false` = categoría oculta al comprador → badge en la grilla de contar. */
    categoryActive: boolean;
    /** Orden de visualización ENTRE docs de categoría. */
    sortOrder: number;
    /** Orden de visualización DENTRO de la categoría. */
    products: InventoryCountSnapshotProduct[];
  };
}
